import React, { useState, useEffect, useMemo, useRef } from 'react';
import { playRewardSound } from '../lib/rewardSound';
import { Trash2, Filter, Plus, Calendar as CalendarIcon, DollarSign, Tag, X, CheckCircle2, ShieldCheck, Droplet, Wind, Shield, Wrench, Package, Disc, Cog, Search } from 'lucide-react';

const approvedAnimStyles = `
@keyframes et-stampDrop {
  0%   { transform: scale(4) rotate(-20deg); opacity: 0; }
  50%  { transform: scale(0.9) rotate(4deg); opacity: 1; }
  70%  { transform: scale(1.08) rotate(-2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}
@keyframes et-fadeIn {
  0%   { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes et-fadeOut {
  0%   { opacity: 1; }
  100% { opacity: 0; }
}
@keyframes et-particlePop {
  0%   { transform: translate(0, 0) scale(0); opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) scale(1.5); opacity: 0; }
}
@keyframes et-ringExpand {
  0%   { transform: scale(0.3); opacity: 1; }
  100% { transform: scale(4); opacity: 0; }
}
@keyframes et-checkPulse {
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
`;
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { translations } from '../translations';

import '../styles/ExpenseTracker.css';

const getCatIcon = (cat, t, size = 16, color = 'currentColor') => {
    if (!cat) return <Package size={size} color={color} />;
    if ([t.categories.oilChange].includes(cat)) return <Droplet size={size} color={color} />;
    if ([t.categories.filter, t.categories.oilFilter, t.categories.fuelFilter, t.categories.cabinFilter, t.categories.transmissionFilter, t.categories.airFilter].includes(cat)) return <Wind size={size} color={color} />;
    if ([t.categories.insurance].includes(cat)) return <Shield size={size} color={color} />;
    if ([t.categories.carInspection].includes(cat)) return <CheckCircle2 size={size} color={color} />;
    if ([t.categories.repairs].includes(cat)) return <Wrench size={size} color={color} />;
    if ([t.categories.maintenance].includes(cat)) return <Cog size={size} color={color} />;
    if ([t.categories.brakes, t.categories.brakePads, t.categories.brakeRotors].includes(cat)) return <Disc size={size} color={color} />;
    return <Package size={size} color={color} />;
};

const ExpenseTracker = ({
    expenses,
    onAddExpense,
    onDeleteExpense,
    currency = 'USD',
    carId,
    categoryFilter = 'All',
    onCategoryFilterChange,
    language = 'en',
    distanceUnit = 'km',
    isAdding: externalIsAdding,
    setIsAdding: externalSetIsAdding,
    isPro,
    currentMileageKm = null,
}) => {
    const [localIsAdding, setLocalIsAdding] = useState(false);
    const isAdding = externalIsAdding !== undefined ? externalIsAdding : localIsAdding;
    const setIsAdding = externalSetIsAdding || setLocalIsAdding;
    const [showApproved, setShowApproved] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const approvedTimerRef = useRef(null);

    // Pre-fill mileage from the car's live odometer when the form opens
    const isMiles = distanceUnit === 'miles' || distanceUnit === 'mi';
    const preFillMileage = currentMileageKm != null
        ? String(isMiles ? Math.round(currentMileageKm * 0.621371) : Math.round(currentMileageKm))
        : '';

    const [newExpense, setNewExpense] = useState({
        title: '',
        amount: '',
        date: new Date(),
        mileage: preFillMileage,
        category: 'Maintenance',
        subcategory: '',
        brakePosition: '',
        expiryDate: null
    });

    // Re-apply pre-fill whenever the form opens (isAdding switches to true)
    // This ensures stale mileage from a previous session is refreshed.
    useEffect(() => {
        if (isAdding) {
            setNewExpense(prev => ({
                ...prev,
                mileage: prev.mileage === '' ? preFillMileage : prev.mileage,
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdding]);

    const t = translations[language];


    // Build ordered list of category entries (key -> label), excluding 'all' and specific filter types
    const filterTypes = ['oilFilter', 'fuelFilter', 'cabinFilter', 'transmissionFilter', 'airFilter'];
    const brakeTypes = ['brakePads', 'brakeRotors'];
    const categoryEntries = Object.entries(t.categories).filter(([key]) =>
        key !== 'all' && !filterTypes.includes(key) && !brakeTypes.includes(key)
    );

    // Define subcategories for the 'filter' main category
    const subCategories = {
        [t.categories.filter]: filterTypes.map(key => t.categories[key]),
        [t.categories.brakes]: [t.categories.brakePads, t.categories.brakeRotors]
    };

    // Human-readable list for the filter dropdown
    const allCategories = [t.categories.all, ...categoryEntries.map(([, label]) => label)];

    const getCurrencySymbol = (curr) => {
        switch (curr) {
            case 'EUR': return '€';
            case 'GBP': return '£';
            case 'USD':
            default: return '$';
        }
    };

    const currencySymbol = getCurrencySymbol(currency);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!newExpense.title || !newExpense.amount) return;

        // If a subcategory is selected, use it as the final category string
        // Otherwise use the main category
        let finalCategory = subCategories[newExpense.category] && newExpense.subcategory
            ? newExpense.subcategory
            : newExpense.category;

        if (newExpense.category === t.categories.brakes && newExpense.brakePosition) {
            finalCategory += ` (${newExpense.brakePosition})`;
        }

        let mileageToSave = newExpense.mileage ? parseInt(newExpense.mileage) : null;
        if (mileageToSave && (distanceUnit === 'miles' || distanceUnit === 'mi')) {
            mileageToSave = Math.round(mileageToSave / 0.621371);
        }

        const expenseData = {
            title: newExpense.title,
            amount: parseFloat(newExpense.amount),
            date: format(newExpense.date, 'yyyy-MM-dd'),
            mileage: mileageToSave,
            category: finalCategory,
            car_id: carId,
            ...(newExpense.expiryDate && { expiryDate: format(newExpense.expiryDate, 'yyyy-MM-dd') })
        };

        saveExpense(expenseData);
    };

    const saveExpense = async (expense) => {
        onAddExpense(expense);
        setNewExpense({
            title: '',
            amount: '',
            date: new Date(),
            mileage: '',
            category: 'Maintenance',
            subcategory: '',
            brakePosition: '',
            expiryDate: null
        });
        setIsAdding(false);
        // Trigger approved stamp animation
        if (approvedTimerRef.current) clearTimeout(approvedTimerRef.current);
        setShowApproved(true);
        setTimeout(() => playRewardSound(), 250);
        approvedTimerRef.current = setTimeout(() => setShowApproved(false), 2000);

        // Show interstitial ad periodically for free users (e.g., 30% chance on save)
        if (!isPro && Math.random() < 0.3) {
            const { showInterstitialAd } = await import('./Ads');
            await showInterstitialAd();
        }
    };



    const filteredExpenses = (categoryFilter === 'All'
        ? expenses
        : expenses.filter(e => {
            if (categoryFilter === t.categories.filter) {
                return e.category === t.categories.filter || subCategories[t.categories.filter].includes(e.category);
            }
            return e.category === categoryFilter;
        })
    ).filter(e => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            (e.title && e.title.toLowerCase().includes(q)) ||
            (e.category && e.category.toLowerCase().includes(q))
        );
    });

    const totalExpenses = filteredExpenses.reduce((sum, item) => sum + item.amount, 0);

    // allCategories already built above

    return (
        <div className="expense-tracker">
            <style>{approvedAnimStyles}</style>

            {/* Approved Stamp Overlay - fullscreen white */}
            {showApproved && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 99999,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: '#ffffff',
                    animation: 'et-fadeIn 0.3s ease both, et-fadeOut 0.4s ease 1.6s forwards',
                    pointerEvents: 'none',
                }}>
                    {/* Expanding rings */}
                    {[0, 1].map(i => (
                        <div key={i} style={{
                            position: 'absolute',
                            width: '200px', height: '200px',
                            borderRadius: '50%',
                            border: `${3 - i}px solid #22c55e`,
                            opacity: 0.5 - i * 0.15,
                            animation: `et-ringExpand 1.2s ease-out ${0.1 + i * 0.3}s both`,
                        }} />
                    ))}
                    {/* Particles */}
                    {[
                        { tx: '-130px', ty: '-120px', color: '#22c55e', size: '14px' },
                        { tx: '130px', ty: '-120px', color: '#4ade80', size: '10px' },
                        { tx: '-130px', ty: '120px', color: '#86efac', size: '12px' },
                        { tx: '130px', ty: '120px', color: '#22c55e', size: '10px' },
                        { tx: '0px', ty: '-160px', color: '#4ade80', size: '8px' },
                        { tx: '0px', ty: '160px', color: '#86efac', size: '14px' },
                        { tx: '-165px', ty: '0px', color: '#22c55e', size: '10px' },
                        { tx: '165px', ty: '0px', color: '#4ade80', size: '8px' },
                        { tx: '-100px', ty: '-150px', color: '#bbf7d0', size: '8px' },
                        { tx: '100px', ty: '150px', color: '#bbf7d0', size: '8px' },
                    ].map((p, i) => (
                        <div key={i} style={{
                            position: 'absolute',
                            width: p.size, height: p.size,
                            borderRadius: '50%', background: p.color,
                            '--tx': p.tx, '--ty': p.ty,
                            animation: `et-particlePop 1s ease-out ${0.2 + i * 0.04}s both`,
                        }} />
                    ))}
                    {/* Icon */}
                    <div style={{
                        width: '120px', height: '120px',
                        borderRadius: '50%',
                        background: '#22c55e',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 0 60px rgba(34,197,94,0.35)',
                        animation: 'et-checkPulse 0.7s cubic-bezier(0.22,1,0.36,1) 0.15s both',
                        marginBottom: '2rem',
                    }}>
                        <ShieldCheck size={60} color="white" strokeWidth={1.5} />
                    </div>
                    <div style={{
                        color: '#15803d', fontWeight: 900,
                        fontSize: '2.5rem', letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        animation: 'et-stampDrop 0.8s cubic-bezier(0.22,1,0.36,1) 0.3s both',
                    }}>
                        Approved
                    </div>
                    <div style={{
                        color: '#6b7280', fontSize: '1rem',
                        fontWeight: 500, marginTop: '0.5rem',
                        animation: 'et-fadeIn 0.6s ease 0.7s both',
                    }}>
                        Expense saved
                    </div>
                </div>
            )}

            {!isAdding && (
                <>
                    <div className="expense-header">
                        <div>
                            <h2 className="section-title">{t.expenseLog}</h2>
                        </div>
                        <div className="total-display">
                            <span className="total-label">{t.totalSpent}</span>
                            <span className="total-amount">{currencySymbol}{totalExpenses.toFixed(2)}</span>
                        </div>
                    </div>

                    <div style={{ position: 'relative', marginBottom: '1rem' }}>
                        <Search
                            size={16}
                            style={{
                                position: 'absolute',
                                left: '14px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'var(--text-secondary)',
                                pointerEvents: 'none',
                            }}
                        />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder={t?.searchExpenses || 'Search expenses\u2026'}
                            style={{
                                width: '100%',
                                padding: '0.75rem 2.5rem 0.75rem 2.5rem',
                                background: 'var(--bg-secondary)',
                                border: '1.5px solid var(--border-color)',
                                borderRadius: '12px',
                                fontSize: '0.95rem',
                                color: 'var(--text-primary)',
                                outline: 'none',
                                boxSizing: 'border-box',
                                transition: 'border-color 0.2s',
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--ui-accent, #738ADB)'}
                            onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)',
                                    display: 'flex',
                                    padding: 0,
                                }}
                            >
                                <X size={15} />
                            </button>
                        )}
                    </div>
                </>
            )}

            {isAdding && (
                <form className="expense-form" onSubmit={handleSubmit}>
                    <h3 className="form-title">{t.newExpense}</h3>

                    <div className="form-group">
                        <label>{t.title}</label>
                        <input
                            type="text"
                            placeholder={t.placeholders.expenseTitle}
                            value={newExpense.title}
                            onChange={e => setNewExpense({ ...newExpense, title: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t.amount} ({currencySymbol})</label>
                            <input
                                type="number"
                                placeholder={t.placeholders.amount}
                                value={newExpense.amount}
                                onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })}
                                required
                                step="0.01"
                            />
                        </div>

                        <div className="form-group">
                            <label>Mileage ({distanceUnit}) - optional</label>
                            <input
                                type="number"
                                placeholder="e.g., 45000"
                                value={newExpense.mileage}
                                onChange={e => setNewExpense({ ...newExpense, mileage: e.target.value })}
                                step="1"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group date-picker-group">
                            <label>{t.date}</label>
                            <div className="date-input-wrapper">
                                <CalendarIcon size={16} className="date-icon" />
                                <DatePicker
                                    selected={newExpense.date}
                                    onChange={date => setNewExpense({ ...newExpense, date })}
                                    dateFormat="yyyy-MM-dd"
                                    className="custom-datepicker"
                                    required
                                />
                            </div>
                        </div>

                        {(newExpense.category === t.categories.insurance || newExpense.category === t.categories.carInspection) && (
                            <div className="form-group date-picker-group">
                                <label>{t.expiryDate}</label>
                                <div className="date-input-wrapper">
                                    <CalendarIcon size={16} className="date-icon" />
                                    <DatePicker
                                        selected={newExpense.expiryDate}
                                        onChange={date => setNewExpense({ ...newExpense, expiryDate: date })}
                                        dateFormat="yyyy-MM-dd"
                                        className="custom-datepicker"
                                        placeholderText="yyyy-mm-dd"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>{t.category}</label>
                        <div className="category-select">
                            {categoryEntries.map(([key, label]) => (
                                <button
                                    type="button"
                                    key={key}
                                    className={`category-chip ${newExpense.category === label ? 'active' : ''}`}
                                    onClick={() => setNewExpense({ ...newExpense, category: label, subcategory: '', brakePosition: '' })}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>



                    {subCategories[newExpense.category] && (
                        <div className="form-group">
                            <label>{newExpense.category === t.categories.brakes ? t.category : t.filterType}</label>
                            <div className="category-select">
                                {subCategories[newExpense.category].map(subcat => (
                                    <button
                                        type="button"
                                        key={subcat}
                                        className={`category-chip subcategory-chip ${newExpense.subcategory === subcat ? 'active' : ''}`}
                                        onClick={() => setNewExpense({ ...newExpense, subcategory: subcat })}
                                    >
                                        {subcat}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {newExpense.category === t.categories.brakes && newExpense.subcategory && (
                        <div className="form-group">
                            <label>{t.brakePosition}</label>
                            <div className="category-select">
                                {[t.front, t.back, t.frontAndBack].map(pos => (
                                    <button
                                        type="button"
                                        key={pos}
                                        className={`category-chip subcategory-chip ${newExpense.brakePosition === pos ? 'active' : ''}`}
                                        onClick={() => setNewExpense({ ...newExpense, brakePosition: pos })}
                                    >
                                        {pos}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="button" className="cancel-btn" onClick={() => setIsAdding(false)}>{t.cancel}</button>
                        <button type="submit" className="save-btn">{t.saveExpense}</button>
                    </div>
                </form>
            )}

            {!isAdding && (
                <div className="expense-list">
                    {filteredExpenses.length === 0 ? (
                        <div className="empty-state">
                            <p>{t.noExpenses}</p>
                        </div>
                    ) : (
                        filteredExpenses.map(expense => {
                            const matchedKey = Object.keys(t.categories).find(key => 
                                Object.values(translations).some(lang => lang.categories && lang.categories[key] === expense.category)
                            );
                            const displayCategory = matchedKey ? t.categories[matchedKey] : expense.category;

                            return (
                            <div key={expense.id} className="expense-item">
                                <div className="expense-left">
                                    <div className="expense-date">
                                        <span className="month">{new Date(expense.date).toLocaleString(language === 'bg' ? 'bg-BG' : 'en-US', { month: 'short' })}</span>
                                        <span className="day">{new Date(expense.date).getDate()}</span>
                                    </div>
                                    <div className="expense-details">
                                        <span className="expense-title">{expense.title}</span>
                                        <span className="expense-category">
                                            {displayCategory}
                                            {expense.mileage && ` • ${(distanceUnit === 'miles' || distanceUnit === 'mi' ? Math.round(expense.mileage * 0.621371) : expense.mileage).toLocaleString()} ${distanceUnit === 'miles' || distanceUnit === 'mi' ? 'mi' : 'km'}`}
                                        </span>
                                    </div>
                                </div>
                                <div className="expense-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                    <span className="expense-amount">-{currencySymbol}{expense.amount.toFixed(2)}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        {expense.isServiceVerified ? (
                                            <div title={`Verified by ${expense.verifiedBy}`} style={{
                                                display: 'flex', alignItems: 'center', gap: '4px',
                                                color: '#10b981', fontSize: '0.75rem', fontWeight: 600,
                                                padding: '4px 8px', background: 'rgba(16, 185, 129, 0.1)',
                                                borderRadius: '12px', whiteSpace: 'nowrap'
                                            }}>
                                                <CheckCircle2 size={14} />
                                                <span>{expense.verifiedBy}</span>
                                            </div>
                                        ) : (
                                            <button className="delete-btn" onClick={() => onDeleteExpense(expense.id)}>
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                        <div style={{ color: 'var(--text-secondary)', display: 'flex' }} title={displayCategory}>
                                            {getCatIcon(displayCategory, t, 17, 'currentColor')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default ExpenseTracker;
